import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../../../utils/currency';
import { resolveProductMediaUrl } from '../../../services/apiService';
import { useThemeClasses } from '../../../hooks/useThemeClasses';

function AlternativeCard({ item, badge, badgeClass, isDark }) {
  const navigate = useNavigate();
  const img = resolveProductMediaUrl(item.imageUrl);

  return (
    <button
      type="button"
      onClick={() => !item.isCurrent && navigate(`/catalog/body-p-${item.listingId}`)}
      disabled={item.isCurrent}
      className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
        item.isCurrent
          ? isDark
            ? 'border-amber-500/50 bg-amber-950/20 cursor-default'
            : 'border-amber-300 bg-amber-50 cursor-default'
          : isDark
            ? 'border-slate-700/60 bg-slate-900/40 hover:border-slate-500'
            : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
      }`}
    >
      <img
        src={img || 'https://via.placeholder.com/80'}
        alt=""
        className={`h-14 w-14 shrink-0 rounded-lg object-cover ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}
        onError={(e) => {
          e.currentTarget.src = 'https://via.placeholder.com/80?text=Part';
        }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${badgeClass}`}>
            {badge}
          </span>
          {item.isCurrent && (
            <span className={`text-[10px] font-semibold ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
              This listing
            </span>
          )}
        </div>
        <p className={`mt-1 truncate text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
          {item.name}
        </p>
        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {item.sellerColorName || '—'} · {formatPrice(item.price)}
        </p>
        {item.repaintCostRange?.label && (
          <p className={`mt-0.5 text-xs ${isDark ? 'text-amber-300/90' : 'text-amber-700'}`}>
            Est. repaint: {item.repaintCostRange.label}
          </p>
        )}
      </div>
    </button>
  );
}

const BADGE_STYLES = {
  exact: {
    dark: 'border border-emerald-600/50 bg-emerald-950/40 text-emerald-200',
    light: 'border border-emerald-200 bg-emerald-50 text-emerald-800',
  },
  painted: {
    dark: 'border border-amber-600/50 bg-amber-950/40 text-amber-200',
    light: 'border border-amber-200 bg-amber-50 text-amber-900',
  },
  other: {
    dark: 'border border-slate-600 bg-slate-800/60 text-slate-300',
    light: 'border border-slate-200 bg-slate-100 text-slate-700',
  },
};

export default function BodyAlternativesSection({ listing }) {
  const { isDark, sc } = useThemeClasses();
  const exact = listing?.exactMatch || [];
  const painted = listing?.canBePainted || [];
  const hasColor = listing?.colorDataAvailable !== false && listing?.vehicleColor;

  if (!exact.length && !painted.length) return null;

  const badgeClass = (key) => BADGE_STYLES[key][isDark ? 'dark' : 'light'];

  return (
    <div className={`space-y-4 ${sc('pdpPanel')} p-4 sm:p-5`}>
      {exact.length > 0 && (
        <section>
          <h3 className={`mb-3 text-sm font-bold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
            Exact Match
          </h3>
          <div className="max-h-[min(24rem,50vh)] space-y-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
            {exact.map((item) => (
              <AlternativeCard
                key={item.listingId}
                item={item}
                badge="Exact Match"
                badgeClass={badgeClass('exact')}
                isDark={isDark}
              />
            ))}
          </div>
        </section>
      )}

      {hasColor && painted.length > 0 && (
        <section>
          <h3 className={`mb-3 text-sm font-bold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
            Can Be Painted
          </h3>
          <div className="max-h-[min(20rem,40vh)] space-y-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
            {painted.map((item) => (
              <AlternativeCard
                key={item.listingId}
                item={item}
                badge="Can Be Painted"
                badgeClass={badgeClass('painted')}
                isDark={isDark}
              />
            ))}
          </div>
        </section>
      )}

      {!hasColor && painted.length > 0 && (
        <section>
          <div className="max-h-[min(20rem,40vh)] space-y-2 overflow-y-auto pr-1 [scrollbar-width:thin]">
            {painted.map((item) => (
              <AlternativeCard
                key={item.listingId}
                item={item}
                badge="Other"
                badgeClass={badgeClass('other')}
                isDark={isDark}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
